/**
 * micSquelch — briefly mute the published microphone track while our
 * own UI sounds (message ping, channel-join chord, ringtone, etc.) are
 * playing through the speakers, so they don't leak via the acoustic
 * path back into the call.
 *
 * Why this exists (OSPROD-74):
 *   The user's mic captures speaker audio. The browser's AEC plus our
 *   RNNoise filter remove most of it, but melodic UI sounds (ringtone,
 *   knock, the Discord-like join chord) are voice-like enough to slip
 *   through, and other call participants hear our local UI through
 *   the user's mic. We *know* exactly when a UI sound plays and for
 *   how long — sounds.ts triggers them — so the cleanest preventive is
 *   to drop a few hundred milliseconds of mic audio for the duration
 *   of the playback.
 *
 * Why MediaStreamTrack.enabled = false:
 *   It's synchronous, costs nothing, works regardless of whether
 *   RNNoise is engaged (it operates on the FINAL published track), and
 *   LiveKit publishes the silence frames transparently — no track
 *   unpublish, no SFU round-trip, no perceptible reconnect.
 *
 * The trade-off: if the user is mid-sentence when a UI sound fires,
 * their speech is also clipped for that window. UI sounds in this app
 * are <0.5s for everything except the periodic ringtone (which only
 * plays before a call is connected, when there's nobody to talk to
 * yet), so the cut is rarely audible.
 */

import { Track, type Room } from 'livekit-client'
import { useLivekitStore } from '../stores/livekitStore'
import { useDmCallStore } from '@os/messenger/web/stores/dmCallStore'

interface ActiveSquelch {
  tracks: MediaStreamTrack[]
  /** Tracks that were already disabled before we touched them — we
   *  must NOT re-enable those, the user has them muted on purpose. */
  preservedDisabled: Set<MediaStreamTrack>
  releaseAt: number
  timer: ReturnType<typeof setTimeout>
}

let active: ActiveSquelch | null = null

// OSPROD-185: is the local user actively talking on any live room?
// LiveKit computes `isSpeaking` from the published audio level with a
// short hangover, so natural inter-word gaps don't flip it off.
function isLocalSpeaking(): boolean {
  const speaking = (room: Room | null | undefined): boolean => {
    if (!room) return false
    try { return !!room.localParticipant.isSpeaking } catch { return false }
  }
  try { if (speaking(useLivekitStore.getState().room)) return true } catch { /* store not ready */ }
  try { if (speaking(useDmCallStore.getState().room)) return true } catch { /* store not ready */ }
  return false
}

function collectMicTracks(): MediaStreamTrack[] {
  const tracks: MediaStreamTrack[] = []
  const collect = (room: Room | null | undefined) => {
    if (!room) return
    try {
      const pub = room.localParticipant.getTrackPublication(Track.Source.Microphone)
      const t = pub?.track?.mediaStreamTrack
      if (t && t.readyState === 'live') tracks.push(t)
    } catch {
      // Room can transition between published / unpublished while we
      // read — any failure here just means we have nothing to squelch.
    }
  }
  try { collect(useLivekitStore.getState().room) } catch { /* store not ready */ }
  try { collect(useDmCallStore.getState().room) } catch { /* store not ready */ }
  return tracks
}

/**
 * Disable any currently-published microphone tracks for `durationMs`
 * milliseconds, then re-enable them. Overlapping calls extend the
 * release deadline rather than chaining timers — the longest deadline
 * wins, which is what we want when several short sounds queue up.
 *
 * No-op when no call is active or all mic tracks are already disabled
 * (user is muted) — there's nothing to leak in either case.
 */
export function squelchMicForMs(durationMs: number): void {
  if (durationMs <= 0) return

  // OSPROD-185: never clip the mic while the user is actively SPEAKING —
  // join/leave tones in busy rooms were chopping fragments out of the
  // speaker's sentences for every listener. Mid-speech the UI tone is
  // acoustically masked by the voice and the browser AEC (which has the
  // page's own output as its reference) removes most of the leak anyway.
  // OSPROD-74's clearly-audible residual was the SILENT-open-mic case,
  // which still squelches below. Skipping (not extending) also keeps the
  // mic live, so isSpeaking stays fresh for the next sound in a burst.
  if (isLocalSpeaking()) return

  const newReleaseAt = performance.now() + durationMs

  if (active) {
    // Extend an in-flight squelch instead of starting a fresh one. Keep
    // the same timer if the new deadline isn't later — saves a clear/
    // reset round-trip for tightly spaced sounds.
    if (newReleaseAt <= active.releaseAt) return
    clearTimeout(active.timer)
    active.releaseAt = newReleaseAt
    active.timer = setTimeout(release, durationMs)
    return
  }

  const tracks = collectMicTracks()
  if (tracks.length === 0) return

  const preservedDisabled = new Set<MediaStreamTrack>()
  for (const t of tracks) {
    if (!t.enabled) {
      // User is muted on this track — leave it alone, and remember not
      // to flip it on at release time.
      preservedDisabled.add(t)
      continue
    }
    t.enabled = false
  }

  active = {
    tracks,
    preservedDisabled,
    releaseAt: newReleaseAt,
    timer: setTimeout(release, durationMs),
  }
}

function release(): void {
  if (!active) return
  for (const t of active.tracks) {
    if (active.preservedDisabled.has(t)) continue
    if (t.readyState !== 'live') continue
    t.enabled = true
  }
  active = null
}
